/**
 * ⚠️ It's unfortunate that this file is in the `lscript` workspace. It should be
 * extracted into a lower-level workspace focused on SQL templating. It's here
 * today because of circular dependencies with LScript; once those are broken,
 * we can and should move it.
 */
import assertNever from '@watershed/shared-util/src/assertNever';

import { doubleQuote, SqlPrimitive, valueToSql } from './sql';
import { randomString } from './tmpCopied/randomString';

export class Identifier {
  constructor(readonly name: string) {}

  minimallyQuoted(): string {
    // If the identifier ends with a $ it might cause parsing wierdness in a
    // dollar-string context, so quote it just to be safe
    if (
      this.name !== 'default' &&
      /^[a-z_][a-z0-9_$]*$/i.test(this.name) &&
      !this.name.endsWith('$')
    ) {
      return this.name;
    }
    return doubleQuote(this.name);
  }

  escape(state: SqlParseState): string {
    const quoted = this.minimallyQuoted();
    switch (state.t) {
      case 'statement':
        return quoted;
      case 'line-comment':
        if (quoted.includes('\n')) {
          throw new Error(
            `Identifier ${quoted} cannot be safely included in a line comment context`
          );
        }
        return quoted;
      case 'block-comment':
        if (quoted.includes('/*') || quoted.includes('*/')) {
          throw new Error(
            `Identifier ${quoted} cannot be safely included in a block comment context`
          );
        }
        return quoted;
      case 'identifier':
        return this.name.replaceAll(/"/g, '""');
      case 'string':
        return quoted.replaceAll(/'/g, "''");
      case 'dollar-string':
        if (quoted.includes(`$${state.delimiter}$`)) {
          throw new Error(
            `Identifier ${quoted} cannot be safely included in a $${state.delimiter}$-delimited dollar quoted string context`
          );
        }
        return quoted;
      default:
        assertNever(state);
    }
  }
}

export class RawSQL {
  constructor(readonly fn: (state: SqlParseState) => string) {}
}

// Table and column names are examples of SQL identifiers. These values are
// quoted using double quotes (c.f., single quotes for string values).
export function sqlIdentifier(name: string): Identifier {
  return new Identifier(name);
}

export function uniqueIdentifier(prefix: string): Identifier {
  return new Identifier(prefix + '_' + randomString());
}

export type Fragment = SqlPrimitive | Identifier | RawSQL;

// If you'd like to take the safety of your SQL into your own hands for an
// advanced use case, you can use this function to inject a raw SQL fragment
// into a larger query. The library code makes several assumptions which you,
// the caller of this function, are responsible for ensuring remain true:
// - The SQL is not vulnerable to SQL injection attacks
// - This fragment closes every new quoting context it opens (said a different
//   way, it is entirely self-contained, with no dangling quotes). By quoting
//   contexts, we mean comments and quotes of all sorts.
export function dangerousRawSql(sql: string): RawSQL {
  // Helper for the most common case: a pre-escaped string usable at the
  // statement level. If you'd like to extend this function, I'd instead
  // recommend making a second function that uses the same underlying RawSQL
  // mechanism.
  return new RawSQL((state) => {
    if (state.t !== 'statement') {
      throw new Error(
        `dangerousRawSql can only be used at the statement level, not ${state.t}`
      );
    }
    return sql;
  });
}

type SqlParseState =
  | { t: 'statement' }
  | { t: 'line-comment' }
  | { t: 'block-comment'; level: number }
  | { t: 'identifier' }
  | { t: 'string' }
  | { t: 'dollar-string'; delimiter: string };

// indexOf is the poor man's lexer. Rewrite -1 to POSITIVE_INFINITY to make
// Math.min(...) work
function lex(str: string, needle: string, start: number): number {
  const i = str.indexOf(needle, start);
  if (i === -1) {
    return Number.POSITIVE_INFINITY;
  }
  return i;
}

// Incrementally "parse" a Postgres or DuckDB SQL statement. There are airquotes
// around "parse" here because we only care about the syntax of SQL insofar as
// we want to know what syntactic element we're currently in so we can properly
// escape it. That basically means quoting and comments.
//
// For more on Postgres' SQL syntax, read:
// https://www.postgresql.org/docs/current/sql-syntax-lexical.html
//
// Don't use this function directly--it's only exported so it can be unit tested
// independently
export function incrementalParseSql(
  state: SqlParseState,
  str: string
): SqlParseState {
  let i = 0;
  while (i < str.length) {
    switch (state.t) {
      case 'statement': {
        const lineCommentI = lex(str, '--', i);
        const blockCommentI = lex(str, `/*`, i);
        const identifierI = lex(str, `"`, i);
        const stringI = lex(str, `'`, i);
        const dollarI = lex(str, '$', i);
        const nextToken = Math.min(
          lineCommentI,
          blockCommentI,
          identifierI,
          stringI,
          dollarI
        );
        if (nextToken === Number.POSITIVE_INFINITY) {
          return state;
        } else if (nextToken === lineCommentI) {
          i = lineCommentI + 2;
          state = { t: 'line-comment' };
        } else if (nextToken === blockCommentI) {
          i = blockCommentI + 2;
          state = { t: 'block-comment', level: 1 };
        } else if (nextToken === identifierI) {
          i = identifierI + 1;
          state = { t: 'identifier' };
        } else if (nextToken === stringI) {
          i = stringI + 1;
          state = { t: 'string' };
        } else if (nextToken === dollarI) {
          // This state requires a bit more work because dollar signs are valid
          // in identifiers
          const match = str.slice(i).match(/(?<![$\w])\$(\w*)\$/);
          if (match?.index == null) {
            i = dollarI + 1;
          } else {
            i = i + match.index + match[0].length;
            state = { t: 'dollar-string', delimiter: match[1] };
          }
        } else {
          throw new Error('unexpected case');
        }
        break;
      }
      case 'line-comment': {
        const newline = str.indexOf(`\n`, i);
        if (newline === -1) {
          return state;
        }
        i = newline + 1;
        state = { t: 'statement' };
        break;
      }
      case 'block-comment': {
        // Unlike C, block comments in SQL can be nested
        const blockCommentI = lex(str, `/*`, i);
        const endBlockCommentI = lex(str, `*/`, i);
        if (blockCommentI < endBlockCommentI) {
          // If we see another opening block comment, increment the nesting
          // level
          i = blockCommentI + 2;
          state = { t: 'block-comment', level: state.level + 1 };
        } else if (endBlockCommentI < blockCommentI) {
          // If we see a closing block comment, reduce the nesting level and
          // possibly return us to statement level
          i = endBlockCommentI + 2;
          if (state.level === 1) {
            state = { t: 'statement' };
          } else {
            state = { t: 'block-comment', level: state.level - 1 };
          }
        } else {
          return state;
        }
        break;
      }
      case 'identifier': {
        const identifierI = lex(str, `"`, i);
        const escapedDoubleQuoteI = lex(str, `""`, i);
        const nextToken = Math.min(identifierI, escapedDoubleQuoteI);
        if (nextToken === Number.POSITIVE_INFINITY) {
          return state;
        } else if (nextToken === escapedDoubleQuoteI) {
          i = escapedDoubleQuoteI + 2;
        } else if (nextToken === identifierI) {
          i = identifierI + 1;
          state = { t: 'statement' };
        } else {
          throw new Error('impossible: how did we get here');
        }
        break;
      }
      case 'string': {
        const stringI = lex(str, `'`, i);
        const escapedSingleQuoteI = lex(str, `''`, i);
        const nextToken = Math.min(stringI, escapedSingleQuoteI);
        if (nextToken === Number.POSITIVE_INFINITY) {
          return state;
        } else if (nextToken === escapedSingleQuoteI) {
          i = escapedSingleQuoteI + 2;
        } else if (nextToken === stringI) {
          i = stringI + 1;
          state = { t: 'statement' };
        } else {
          throw new Error('impossible: how did we get here');
        }
        break;
      }
      case 'dollar-string': {
        const endDollarStringI = str.indexOf(`$${state.delimiter}$`, i);
        if (endDollarStringI === -1) {
          return state;
        }
        i = endDollarStringI + state.delimiter.length + 2;
        state = { t: 'statement' };
        break;
      }
      default:
        assertNever(state);
    }
  }

  return state;
}

function syntaxSafeJoin(state: SqlParseState, a: string, b: string): string {
  switch (state.t) {
    case 'statement': {
      // Postgres supports some string prefixes with different escaping rules:
      // https://www.postgresql.org/docs/current/sql-syntax-lexical.html
      if (
        a.endsWith('E') ||
        a.endsWith('e') ||
        a.endsWith('U&') ||
        a.endsWith('u&')
      ) {
        if (b.startsWith('"') || b.startsWith("'")) {
          return a + ' ' + b;
        }
      }
      // These are harder to introduce into a statement context, because these
      // symbols mostly can't appear in identifiers, and strings are always
      // quoted in this context, but better safe than sorry!
      const patterns: Array<[string, string]> = [
        ['-', '-'],
        ['/', '*'],
        ['*', '/'],
        ['$', '$'],
      ];
      for (const [aSuffix, bPrefix] of patterns) {
        if (a.endsWith(aSuffix) && b.startsWith(bPrefix)) {
          return a + ' ' + b;
        }
      }
      return a + b;
    }
    case 'line-comment':
      return a + b;
    case 'block-comment': {
      const patterns: Array<[string, string]> = [
        ['/', '*'],
        ['*', '/'],
      ];
      for (const [aSuffix, bPrefix] of patterns) {
        if (a.endsWith(aSuffix) && b.startsWith(bPrefix)) {
          throw new Error(
            `Cannot safely combine ${JSON.stringify(a)} and ${JSON.stringify(
              b
            )} in a block-comment context`
          );
        }
      }
      return a + b;
    }
    case 'identifier':
      return a + b;
    case 'string':
      return a + b;
    case 'dollar-string': {
      // This one is more complicated:
      // - Search backwards in A for the delimiter. This gives us the start of
      //   the dollar-delimited string
      // - Search forwards from that point in the combined string for the
      //   delimiter. This gives us the end of the string, if it exists (it
      //   might be in a subsequent fragment)
      // - If that point straddles the A/B boundary, combining A and B has
      //   produced a false boundary (bad!)
      const delim = `$${state.delimiter}$`;
      const quoteStart = a.lastIndexOf(delim);
      if (quoteStart === -1) {
        throw new Error('impossible: how did this string start?');
      }
      const stringStart = quoteStart + delim.length;
      const out = a + b;
      const stringEnd = out.indexOf(delim, stringStart);
      if (
        stringEnd !== -1 &&
        stringEnd < a.length &&
        stringEnd + delim.length >= b.length
      ) {
        throw new Error(
          `Cannot safely combine ${JSON.stringify(a)} and ${JSON.stringify(
            b
          )} in a dollar-string context with delim=${state.delimiter}`
        );
      }
      return out;
    }
    default:
      assertNever(state);
  }
}

export function sql(
  strings: TemplateStringsArray,
  ...exprs: Array<Fragment>
): string {
  let state: SqlParseState = { t: 'statement' };
  let out = '';

  for (let i = 0; i < strings.length; i++) {
    out = syntaxSafeJoin(state, out, strings[i]);
    state = incrementalParseSql(state, strings[i]);
    if (i < exprs.length) {
      let str = '';
      const expr = exprs[i];
      if (expr instanceof Identifier) {
        str += expr.escape(state);
      } else if (expr instanceof RawSQL) {
        str += expr.fn(state);
      } else {
        const quoted = valueToSql(expr);
        switch (state.t) {
          case 'statement':
            str += quoted;
            break;
          case 'line-comment':
            if (typeof expr === 'string') {
              if (expr.includes('\n')) {
                throw new Error(
                  `Literal ${expr} cannot be safely included in a line comment context`
                );
              }
              str += expr;
            } else {
              str += quoted;
            }
            break;
          case 'block-comment':
            if (typeof expr === 'string') {
              if (expr.includes('/*') || expr.includes('*/')) {
                throw new Error(
                  `Literal ${expr} cannot be safely included in a block comment context`
                );
              }
              str += expr;
            } else {
              str += quoted;
            }
            break;
          case 'identifier':
            if (typeof expr === 'string') {
              str += sqlIdentifier(expr).escape(state);
            } else {
              str += quoted;
            }
            break;
          case 'string':
            if (typeof expr === 'string') {
              str += expr.replaceAll(/'/g, "''");
            } else {
              str += quoted;
            }
            break;
          case 'dollar-string':
            if (typeof expr === 'string') {
              if (expr.includes(`$${state.delimiter}$`)) {
                throw new Error(
                  `Identifier ${expr} cannot be safely included in a $${state.delimiter}$-delimited dollar quoted string context`
                );
              }
              str += expr;
            } else {
              str += quoted;
            }
            break;
          default:
            assertNever(state);
        }
      }
      out = syntaxSafeJoin(state, out, str);
    }
  }

  if (state.t !== 'statement' && state.t !== 'line-comment') {
    // Also ok to be in a line comment at the end of the parse: the line comment
    // will go up until the end of the SQL statement
    throw new Error(
      `SQL parse error: not at 'statement' state at end of parse. Is your statement well-formed? fragments: ${JSON.stringify(
        strings
      )}`
    );
  }

  return out;
}

export function sqlF(
  strings: TemplateStringsArray,
  ...exprs: Array<Fragment>
): RawSQL {
  // eslint-disable-next-line @watershed/no-dangerous-raw-sql -- fine within the library
  return dangerousRawSql(sql(strings, ...exprs));
}

function sqlJoin(arms: ReadonlyArray<RawSQL>, glue: string): string {
  // Per the comment on dangerousRawSql, the arms are presumed to close all of
  // the quoting contexts they open. In order for this function to be safe, we
  // need to ensure:
  // 1. The glue does not introduce any quoting contexts it does not close, and
  // 2. The fragments are glued together in a safe way
  const state = incrementalParseSql({ t: 'statement' }, glue);
  if (state.t !== 'statement') {
    throw new Error(
      `Cannot use ${JSON.stringify(
        glue
      )} as join glue because it introduces quoting contexts it does not close`
    );
  }

  let out = '';
  for (let i = 0; i < arms.length; i++) {
    out = syntaxSafeJoin(state, out, arms[i].fn(state));
    if (i !== arms.length - 1) {
      out = syntaxSafeJoin(state, out, glue);
    }
  }
  return out;
}

export function sqlJoinF(arms: ReadonlyArray<RawSQL>, glue: string): RawSQL {
  // eslint-disable-next-line @watershed/no-dangerous-raw-sql -- fine within the library
  return dangerousRawSql(sqlJoin(arms, glue));
}

export function sqlCaseF<K extends SqlPrimitive>(
  m: ReadonlyMap<K, Fragment>,
  keyExpr: RawSQL,
  default_?: Fragment
): RawSQL {
  if (m.size === 0) {
    // CASE expressions can't have zero WHEN clauses
    return sqlF`${default_ ?? null}`;
  }
  const arms = new Array<RawSQL>();
  for (const [k, v] of m) {
    arms.push(sqlF`WHEN ${k} THEN ${v}`);
  }
  if (default_ !== undefined) {
    arms.push(sqlF`ELSE ${default_}`);
  }
  return sqlF`CASE ${keyExpr} ${sqlJoinF(arms, ' ')} END`;
}
